import { GlobalStyles, setRef } from "@mui/material";
import { F } from "ramda";
import React, { PropsWithChildren, useContext, useEffect, useMemo, useRef, useState } from "react";

interface IntersectionRendererContextProps {
  observer: IntersectionObserver;
  isVisible: (el: Element) => boolean;
}

const IntersectionRendererContext = React.createContext<IntersectionRendererContextProps | undefined>(undefined);
IntersectionRendererContext.displayName = "IntersectionRenderer";

interface IntersectionRendererProviderProps extends PropsWithChildren {
  /**
   * View props
   */
  intersectionObserverOptions?: IntersectionObserverInit;
}

/**
 * Provide context to propagate view props
 */
export function IntersectionRendererProvider({
  children,
  intersectionObserverOptions,
}: IntersectionRendererProviderProps) {
  const visibilityMap = useRef<Map<Element, boolean>>(new Map<Element, boolean>());
  const [isVisibleFn, setIsVisibleFn] = useState<(el: Element) => boolean>(() => F);

  const observer = useMemo(
    () =>
      new IntersectionObserver(
        // visibilityMap.current is read/written inside this async observer callback, never during render
        // oxlint-disable-next-line react-hooks-js/refs
        async (entries) => {
          const modified = entries.reduce(
            (changed, entry) => {
              const previous = visibilityMap.current.get(entry.target);
              if (entry.isIntersecting) {
                visibilityMap.current.set(entry.target, true);
              } else {
                visibilityMap.current.set(entry.target, false);
              }
              return changed || previous !== entry.isIntersecting;
            },
            [false]
          );
          if (modified) {
            setIsVisibleFn(() => (el: Element) => !!visibilityMap.current.get(el));
          }
        },
        intersectionObserverOptions //{ threshold: 0, rootMargin: "30px 0px 30px 0px" }
      ),
    []
  );

  useEffect(() => () => observer.disconnect(), []);

  return (
    <>
      <GlobalStyles styles={{ html: { overflowAnchor: "none" } }} />
      <IntersectionRendererContext.Provider value={{ observer, isVisible: isVisibleFn }}>
        {children}
      </IntersectionRendererContext.Provider>
    </>
  );
}

/**
 * Provide function for get props of view
 */
export function useIntersectionRenderer() {
  const context = useContext(IntersectionRendererContext);
  if (!context) {
    throw new Error("IntersectionRendererProvider is not defined");
  }
  const { observer, isVisible } = context;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hiddenContainerDimensions = useRef<{ width: number; height: number }>({ width: 0, height: 0 });

  useEffect(() => {
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  const setContainerRef = (el: HTMLDivElement | null) => {
    if (el) {
      // Check intersection immediately
      const isIntersecting = checkIntersection(el);
      if (isIntersecting && !isVisible(el)) {
        //setVisible(true); TODO: solve
      }

      if (isVisible(el)) {
        el.style.removeProperty("min-width");
        el.style.removeProperty("min-height");
        const { width, height } = el.getBoundingClientRect();

        // const deltaWidth = width - hiddenContainerDimensions.current.width;
        // const deltaHeight = height - hiddenContainerDimensions.current.height;
        // compensation for change of size of rendered element
        //globalThis.scrollBy(deltaWidth, deltaHeight);

        // Remember last dimensions
        el.style.minWidth = width + "px";
        el.style.minHeight = height + "px";
      } else {
        const { width, height } = el.getBoundingClientRect();
        hiddenContainerDimensions.current = { width, height };
      }
    }
    setRef(containerRef, el);
  };

  // visibility is recomputed on every render; isVisibleFn state changes (from the observer) drive the updates
  // oxlint-disable-next-line react-hooks-js/refs
  return { containerRef: setContainerRef, visible: containerRef.current ? isVisible(containerRef.current) : false };
}
const checkIntersection = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  const windowHeight = globalThis.innerHeight;
  const windowWidth = globalThis.innerWidth;

  return rect.top < windowHeight && rect.bottom > 0 && rect.left < windowWidth && rect.right > 0;
};
